import React, { useEffect, useRef } from 'react';

// Skip to content link for keyboard navigation
export const SkipToContent = () => (
  <a
    href="#main-content"
    className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white text-black px-4 py-2 rounded-md z-50 font-medium shadow-lg border-2 border-rose-500"
    tabIndex={0}
  >
    Skip to main content
  </a>
);

// Enhanced focus management hook
export const useFocusManagement = (isOpen = false, closeCallback = null) => {
  const previousFocusRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousFocusRef.current = document.activeElement;
      
      // Focus the container or first focusable element
      if (containerRef.current) {
        const focusableElement = containerRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElement) {
          focusableElement.focus();
        } else {
          containerRef.current.focus();
        }
      }
    } else if (previousFocusRef.current) {
      // Restore focus when closing
      previousFocusRef.current.focus();
    }

    // Handle escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && closeCallback) {
        closeCallback();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeCallback]);

  return containerRef;
};

// Enhanced button with accessibility features
export const AccessibleButton = ({ 
  children, 
  onClick, 
  disabled = false, 
  ariaLabel, 
  ariaDescribedBy,
  variant = 'primary',
  size = 'md',
  className = '',
  loading = false,
  ...props 
}) => {
  const baseClasses = "font-medium rounded-lg transition-all focus:outline-none focus:ring-4 focus:ring-offset-2";
  
  const variants = {
    primary: "bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white focus:ring-rose-300",
    secondary: "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 focus:ring-gray-300",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-300"
  };
  
  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-busy={loading}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className} ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      {...props}
    >
      {loading && (
        <span className="mr-2" aria-hidden="true">
          <svg className="animate-spin h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </span>
      )}
      {children}
    </button>
  );
};

// Enhanced form input with accessibility
export const AccessibleInput = ({
  label,
  id,
  type = 'text',
  value,
  onChange,
  error,
  required = false,
  placeholder,
  ariaDescribedBy,
  className = '',
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = error ? `${inputId}-error` : undefined;
  
  return (
    <div className={`space-y-2 ${className}`}>
      <label 
        htmlFor={inputId} 
        className="block text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-red-500 ml-1" aria-label=" (required)">*</span>}
      </label>
      
      <input
        id={inputId}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-required={required}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={[ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined}
        className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-4 focus:ring-offset-2 transition-all ${
          error 
            ? 'border-red-500 focus:ring-red-300 focus:border-red-500' 
            : 'border-gray-300 focus:ring-rose-300 focus:border-rose-500'
        }`}
        {...props}
      />
      
      {error && (
        <p id={errorId} className="text-sm text-red-600" role="alert">
          <span className="sr-only">Error: </span>
          {error}
        </p>
      )}
    </div>
  );
};

// Screen reader only text
export const ScreenReaderOnly = ({ children, className = '' }) => (
  <span className={`sr-only ${className}`}>{children}</span>
);

// Accessible modal wrapper
export const AccessibleModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  className = '' 
}) => {
  const modalRef = useFocusManagement(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          aria-hidden="true"
          onClick={onClose}
        />

        {/* Modal content */}
        <div
          ref={modalRef}
          className={`inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl ${className}`}
          tabIndex={-1}
        >
          <h2 id="modal-title" className="text-lg font-medium text-gray-900 mb-4">
            {title}
          </h2>
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-rose-500 rounded-full p-1"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {children}
        </div>
      </div>
    </div>
  );
};

// Accessible navigation menu
export const AccessibleNavigation = ({ items, currentPath, className = '' }) => (
  <nav className={className} role="navigation" aria-label="Main navigation">
    <ul className="flex space-x-4">
      {items.map((item, index) => {
        const isActive = currentPath === item.path;
        return (
          <li key={item.path}>
            <a
              href={item.path}
              className={`px-3 py-2 rounded-lg font-medium transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-rose-300 ${
                isActive
                  ? 'bg-rose-100 text-rose-800'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-current={isActive ? 'page' : undefined}
            >
              {item.label}
            </a>
          </li>
        );
      })}
    </ul>
  </nav>
);

// Enhanced image with proper accessibility
export const AccessibleImage = ({ 
  src, 
  alt, 
  className = '', 
  loading = 'lazy',
  decorative = false,
  ...props 
}) => (
  <img
    src={src}
    alt={decorative ? '' : alt}
    className={className}
    loading={loading}
    role={decorative ? 'presentation' : undefined}
    aria-hidden={decorative ? 'true' : undefined}
    {...props}
  />
);

// Accessible card component
export const AccessibleCard = ({ 
  children, 
  title,
  description,
  onClick,
  href,
  className = '',
  focusable = true
}) => {
  const Component = href ? 'a' : onClick ? 'button' : 'div';
  
  return (
    <Component
      href={href}
      onClick={onClick}
      className={`block w-full text-left rounded-lg border border-gray-200 p-6 shadow-sm transition-all hover:shadow-md ${
        (onClick || href) && focusable ? 'focus:outline-none focus:ring-4 focus:ring-rose-300 focus:ring-offset-2' : ''
      } ${className}`}
      tabIndex={focusable && (onClick || href) ? 0 : undefined}
      role={onClick && !href ? 'button' : undefined}
      aria-label={title && description ? `${title}: ${description}` : title}
    >
      {children}
    </Component>
  );
};

export default {
  SkipToContent,
  useFocusManagement,
  AccessibleButton,
  AccessibleInput,
  ScreenReaderOnly,
  AccessibleModal,
  AccessibleNavigation,
  AccessibleImage,
  AccessibleCard
};